import { db } from '../../src/firebase.js';
import {
    collection,
    doc,
    getDocs,
    setDoc,
    deleteDoc,
    query,
    where,
    serverTimestamp,
} from 'firebase/firestore';
import { getCurrentUser } from '../data/auth.js';

const LS_PROJECTS = 'fg_fund_projects';
const LS_DRAFTS = 'fg_fund_drafts';

export function slugify(...parts) {
    return parts
        .filter(Boolean)
        .join('-')
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-|-$/g, '')
        .slice(0, 48) || 'campaign';
}

export function uniqueCampaignId(creator, title, existingIds) {
    const base = slugify(creator, title);
    let id = base;
    let n = 0;
    while (existingIds.has(id)) {
        n += 1;
        id = `${base}-${n}`;
    }
    return id;
}

export function loadLocalProjects() {
    try {
        const raw = localStorage.getItem(LS_PROJECTS);
        return raw ? JSON.parse(raw) : [];
    } catch {
        return [];
    }
}

export function saveLocalProjects(projects) {
    localStorage.setItem(LS_PROJECTS, JSON.stringify(projects));
}

export function loadLocalDrafts() {
    try {
        return JSON.parse(localStorage.getItem(LS_DRAFTS) || '[]');
    } catch {
        return [];
    }
}

export function saveLocalDrafts(drafts) {
    localStorage.setItem(LS_DRAFTS, JSON.stringify(drafts));
}

export function getCampaignShareUrl(id) {
    const origin = typeof window !== 'undefined' ? window.location.origin : '';
    return `${origin}/#page=fund&project=${encodeURIComponent(id)}`;
}

export function isCampaignClosed(project) {
    if (project.status === 'closed') return true;
    if (!project.endDate) return false;
    return new Date(project.endDate) < new Date();
}

export function normalizeProject(p) {
    return {
        ...p,
        status: p.status || 'live',
        slug: p.slug || p.id,
        images: p.images || (p.imageURL ? [p.imageURL] : []),
        imageURL: p.imageURL || p.images?.[0] || '',
        faqs: p.faqs || [],
        bodyHtml: p.bodyHtml || p.fullStory || p.description || '',
        rewardTiers: p.rewardTiers || [],
        updates: p.updates || [],
        comments: p.comments || [],
        followers: p.followers || [],
        raised: p.raised || 0,
        donorsCount: p.donorsCount || 0,
        currency: p.currency || 'eur',
        stripeAccountId: p.stripeAccountId || null,
        stripeVerified: !!p.stripeVerified,
        financialLocked: !!p.financialLocked,
        comingSoonEnabled: !!p.comingSoonEnabled,
        previewToken: p.previewToken || null,
        previewTokenExpires: p.previewTokenExpires || null,
        collaborators: p.collaborators || [],
        communityComments: p.communityComments || [],
        kycVerified: !!p.kycVerified,
        notifyOnComment: p.notifyOnComment !== false,
    };
}

/** Merge Firestore live campaigns with localStorage (local wins on id conflict for same user edits) */
export async function loadCampaignsForOwner(uid) {
    if (!uid) return [];
    const local = loadLocalProjects()
        .map(normalizeProject)
        .filter((p) => p.ownerUid === uid);
    try {
        const snap = await getDocs(
            query(collection(db, 'fund_campaigns'), where('ownerUid', '==', uid))
        );
        const remote = snap.docs.map((d) => normalizeProject({ id: d.id, ...d.data() }));
        const byId = new Map(local.map((p) => [p.id, p]));
        remote.forEach((p) => {
            if (!byId.has(p.id)) byId.set(p.id, p);
        });
        return [...byId.values()];
    } catch {
        return local;
    }
}

export async function loadAllCampaigns() {
    const local = loadLocalProjects().map(normalizeProject);
    const user = getCurrentUser();
    if (!user) return local;

    try {
        const snap = await getDocs(
            query(collection(db, 'fund_campaigns'), where('status', '==', 'live'))
        );
        const remote = snap.docs.map((d) => normalizeProject({ id: d.id, ...d.data() }));
        const byId = new Map(local.map((p) => [p.id, p]));
        remote.forEach((p) => {
            if (!byId.has(p.id)) byId.set(p.id, p);
        });
        return [...byId.values()];
    } catch (err) {
        console.warn('[Fund] Firestore load failed, using local only', err);
        return local;
    }
}

export async function persistCampaign(campaign) {
    const normalized = normalizeProject(campaign);
    const projects = loadLocalProjects();
    const idx = projects.findIndex((p) => p.id === normalized.id);
    if (idx >= 0) projects[idx] = normalized;
    else projects.push(normalized);
    saveLocalProjects(projects);

    const user = getCurrentUser();
    if (user) {
        try {
            await setDoc(
                doc(db, 'fund_campaigns', normalized.id),
                {
                    ...normalized,
                    ownerUid: user.uid,
                    updatedAt: serverTimestamp(),
                },
                { merge: true }
            );
        } catch (err) {
            console.warn('[Fund] Firestore save failed', err);
        }
    }
    return normalized;
}

export async function upsertDraft(draft) {
    const user = getCurrentUser();
    const drafts = loadLocalDrafts();
    const id = draft.draftId || `draft-${Date.now()}`;
    const entry = { ...draft, draftId: id, updatedAt: Date.now() };
    const idx = drafts.findIndex((d) => d.draftId === id);
    if (idx >= 0) drafts[idx] = entry;
    else drafts.push(entry);
    saveLocalDrafts(drafts);

    if (user) {
        try {
            await setDoc(doc(db, 'fund_campaigns', id), {
                ...entry,
                status: 'draft',
                ownerUid: user.uid,
                updatedAt: serverTimestamp(),
            }, { merge: true });
        } catch (err) {
            console.warn('[Fund] draft save failed', err);
        }
    }
    return entry;
}

export async function deleteDraftById(draftId) {
    saveLocalDrafts(loadLocalDrafts().filter((d) => d.draftId !== draftId));
    const user = getCurrentUser();
    if (user) {
        try {
            await deleteDoc(doc(db, 'fund_campaigns', draftId));
        } catch (_) { /* ignore */ }
    }
}

export function loadDraftsForUser() {
    const user = getCurrentUser();
    const drafts = loadLocalDrafts();
    if (!user) return drafts;
    return drafts.filter((d) => !d.ownerUid || d.ownerUid === user.uid);
}

export function getPlatformFeePercentClient() {
    const raw = import.meta.env.VITE_PLATFORM_FEE_PERCENT;
    const n = Number(raw);
    return Number.isFinite(n) && n >= 0 ? n : 5;
}

export function calcFeeBreakdown(goalEur, platformPercent = getPlatformFeePercentClient()) {
    const goal = Math.max(0, Number(goalEur) || 0);
    const stripe = goal * 0.029 + 0.3;
    const platform = goal * (platformPercent / 100);
    const net = Math.max(0, goal - stripe - platform);
    return { goal, stripe, platform, net, platformPercent };
}
